import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Observable, tap } from 'rxjs';
import {
  Face,
  LogoDetection,
  NomItem,
  Post,
} from 'src/app/core/models/core.models';

import { PostDetailsService } from 'src/app/core/services/post-details.service';
import { PostService } from 'src/app/core/services/post.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DialogCloseOptions, DialogCloseReason } from '@sui/dialog';
import { DialogService } from '@sui/dialog';
import { ConfirmDialogComponent } from '../../popups/confirm-dialog/confirm-dialog.component';

type FaceRecognitionStatus = 'ignored' | 'recognized' | 'unrecognized';

type FaceMeta = {
  borderColor: string;
  recognitionStatus: FaceRecognitionStatus;
  name: string;
  expandList?: boolean;
};

type LogoMeta = {
  name: string;
  expandList?: boolean;
};

type ImageTaggingItem = {
  displayUrl: string;
  detectionUrl: string;
  faces: Face[];
  logos: LogoDetection[];
  faceMeta: FaceMeta[];
  logoMeta: LogoMeta[];
  scaleFactorWidth: number;
  scaleFactorHeight: number;
  loading: boolean;
};

@Component({
  selector: 'app-post-image-tagging',
  templateUrl: './post-image-tagging.component.html',
  styleUrls: ['./post-image-tagging.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PostImageTaggingComponent implements OnInit, OnChanges {
  @Input() postId: string;

  private destroyRef = inject(DestroyRef);
  private dialogService = inject(DialogService);
  private postDetails = inject(PostDetailsService);

  /** The post this tab is showing - the pane pushes it through `ngOnChanges`. */
  private readonly shownPostId = signal<string | null>(null);

  // `toObservable` needs an injection context, so this is a field; `ngOnInit` only
  // subscribes.
  post$: Observable<Post> = toObservable(
    computed(() => this.postDetails.postFor(this.shownPostId())),
  );
  post: Post;
  imageItems: ImageTaggingItem[] = [];
  suggestList$: Observable<Array<NomItem>>;
  suggestList: Array<NomItem> = [];
  loading: boolean = false;
  logoBorderColor = '#3b82f6';

  constructor(
    private postService: PostService,
    private elementRef: ElementRef,
  ) {}

  ngOnInit() {
    this.post$
      .pipe(
        tap((post) => {
          if (!post) {
            this.resetImageState();
            return;
          }

          this.post = post;
          this.loadImages(post);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    if (this.postId) {
      this.loading = true;
      this.shownPostId.set(this.postId);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['postId']?.currentValue) {
      this.loading = true;
      console.log('[image-tagging] postId change', this.postId);
      this.shownPostId.set(this.postId);
    }
  }

  get hasDetections(): boolean {
    return this.imageItems.some(
      (item) => item.faces.length > 0 || item.logos.length > 0,
    );
  }

  updateImageScale(imageIndex: number, event: Event) {
    const imgElement = event.target as HTMLImageElement;
    const imageItem = this.imageItems[imageIndex];
    if (!imageItem || !imgElement?.naturalWidth || !imgElement?.naturalHeight) {
      return;
    }

    imageItem.scaleFactorWidth = imgElement.width / imgElement.naturalWidth;
    imageItem.scaleFactorHeight = imgElement.height / imgElement.naturalHeight;
  }

  setStatus(imageIndex: number, faceIndex: number, statusId: number) {
    const imageItem = this.imageItems[imageIndex];
    const faceMeta = imageItem?.faceMeta?.[faceIndex];
    if (!imageItem || !faceMeta) {
      return;
    }

    if (statusId === 3 && !faceMeta.name) {
      return;
    }

    const nextStatus = this.getStatusDetails(statusId);
    faceMeta.recognitionStatus = nextStatus.status;
    faceMeta.borderColor = nextStatus.borderColor;
    imageItem.faces[faceIndex].backend_recognition_status_id = statusId;
  }

  save(imageIndex: number, faceIndex: number) {
    const imageItem = this.imageItems[imageIndex];
    const face = imageItem?.faces?.[faceIndex];
    const faceMeta = imageItem?.faceMeta?.[faceIndex];

    if (!imageItem || !face || !faceMeta) {
      return;
    }

    const message = 'Are you sure you want to save?';

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          const faceAssignData = {
            id: face.id,
            person_name: faceMeta.name,
            recognition_status_id:
              imageItem.faces[faceIndex].backend_recognition_status_id,
          };

          this.postService
            .assignFace(this.postId, imageItem.detectionUrl, faceAssignData)
            .subscribe();
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }

  inputChange(target: any, imageIndex: number, faceIndex: number) {
    const imageItem = this.imageItems[imageIndex];
    const faceMeta = imageItem?.faceMeta?.[faceIndex];
    if (!imageItem || !faceMeta) {
      return;
    }

    const query = target.value?.toString() ?? '';
    this.suggestList$ = this.postService.getImageFacesSuggest(query);
    this.suggestList$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((suggestion) => (this.suggestList = suggestion));
    faceMeta.expandList = true;
    faceMeta.name = query;
    if (query.trim().length > 0) {
      this.setStatus(imageIndex, faceIndex, 3);
    } else {
      this.setStatus(imageIndex, faceIndex, 1);
    }
  }

  handleNameClick(event: any, imageIndex: number, faceIndex: number) {
    const imageItem = this.imageItems[imageIndex];
    const faceMeta = imageItem?.faceMeta?.[faceIndex];
    if (!imageItem || !faceMeta) {
      return;
    }

    faceMeta.name = event.target.innerHTML;
    faceMeta.expandList = false;
    this.setStatus(imageIndex, faceIndex, 3);
  }

  logoInputChange(target: any, imageIndex: number, logoIndex: number) {
    const imageItem = this.imageItems[imageIndex];
    const logoMeta = imageItem?.logoMeta?.[logoIndex];
    if (!imageItem || !logoMeta) {
      return;
    }

    const query = target.value?.toString() ?? '';
    logoMeta.name = query;
    logoMeta.expandList = true;
  }

  handleLogoNameClick(event: any, imageIndex: number, logoIndex: number) {
    const imageItem = this.imageItems[imageIndex];
    const logoMeta = imageItem?.logoMeta?.[logoIndex];
    if (!imageItem || !logoMeta) {
      return;
    }

    logoMeta.name = event.target.innerText.trim();
    logoMeta.expandList = false;
  }

  saveLogo(imageIndex: number, logoIndex: number) {
    const imageItem = this.imageItems[imageIndex];
    const logo = imageItem?.logos?.[logoIndex];
    const logoMeta = imageItem?.logoMeta?.[logoIndex];
    if (!imageItem || !logo || !logoMeta) {
      return;
    }

    const brand = logoMeta.name.trim();
    if (!brand) {
      return;
    }

    if (!Array.isArray(logo.embedding) || logo.embedding.length === 0) {
      console.error('Logo embedding is missing for assignLogo');
      return;
    }

    const message = 'Are you sure you want to save?';

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.postService
            .assignLogo(imageItem.detectionUrl, {
              brand,
              embedding: logo.embedding as number[],
            })
            .subscribe({
              error: (error) => {
                console.error('HTTP Error:', error);
              },
            });
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.imageItems.forEach((item) => {
        item.faceMeta.forEach((faceMeta) => {
          faceMeta.expandList = false;
        });
        item.logoMeta.forEach((logoMeta) => {
          logoMeta.expandList = false;
        });
      });
    }
  }

  private loadImages(post: Post) {
    console.log('[image-tagging] post payload', post);
    console.log('[image-tagging] post.attachments', post?.attachments);
    const imageItems = this.getAttachmentImageItems(post);
    this.imageItems = imageItems.map((item) => ({
      displayUrl: item.displayUrl,
      detectionUrl: item.detectionUrl,
      faces: [],
      logos: [],
      faceMeta: [],
      logoMeta: [],
      scaleFactorWidth: 1,
      scaleFactorHeight: 1,
      loading: true,
    }));

    console.log('[image-tagging] imageItems', this.imageItems);

    if (this.imageItems.length === 0) {
      this.loading = false;
      return;
    }

    this.loading = true;
    this.imageItems.forEach((item) => {
      console.log(
        '[image-tagging] request getImageFaces',
        item.detectionUrl,
        this.postId,
      );
      this.postService
        .getImageFaces(item.detectionUrl, this.postId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (faceData) => {
            item.faces = faceData?.faces ?? [];
            item.logos = (faceData?.logos ?? []).map((logo) =>
              this.normalizeLogo(logo),
            );
            item.faceMeta = item.faces.map((face) => this.buildFaceMeta(face));
            item.logoMeta = item.logos.map((logo) => this.buildLogoMeta(logo));
            item.loading = false;
            this.updateLoadingState();
          },
          error: () => {
            item.loading = false;
            this.updateLoadingState();
          },
        });
    });
  }

  private resetImageState() {
    this.imageItems = [];
    this.loading = false;
  }

  private updateLoadingState() {
    this.loading = this.imageItems.some((item) => item.loading);
  }

  private getAttachmentImageItems(
    post: Post,
  ): Array<{ displayUrl: string; detectionUrl: string }> {
    const attachments = this.getPostAttachments(post);
    const imageItems = attachments
      .filter(
        (attachment) =>
          attachment?.type === 'image' ||
          attachment?.type === 'photo' ||
          attachment?.image_src ||
          attachment?.image,
      )
      .map((attachment) => this.getAttachmentImageUrl(attachment))
      .filter((item): item is { displayUrl: string; detectionUrl: string } =>
        Boolean(item),
      );

    const candidateItems = [...imageItems, ...this.getFallbackImageItems(post)];

    if (imageItems.length === 0) {
      console.log('[image-tagging] fallback images', candidateItems);
    }

    const uniqueItems = new Map<
      string,
      { displayUrl: string; detectionUrl: string }
    >();
    candidateItems.forEach((item) => {
      if (!uniqueItems.has(item.detectionUrl)) {
        uniqueItems.set(item.detectionUrl, item);
      }
    });

    return Array.from(uniqueItems.values());
  }

  private getPostAttachments(post: Post): any[] {
    const attachments = post?.attachments;
    if (Array.isArray(attachments)) {
      return attachments;
    }

    if (attachments && typeof attachments === 'object') {
      return Object.values(attachments).flatMap((value) =>
        Array.isArray(value) ? value : [],
      );
    }

    return [];
  }

  private getFallbackImageItems(
    post: Post,
  ): Array<{ displayUrl: string; detectionUrl: string }> {
    const fallbackUrls = [post?.thumbnail_big, post?.thumbnail].filter(
      (url): url is string => typeof url === 'string' && url.trim().length > 0,
    );

    return fallbackUrls.map((url) => this.resolveImageUrls(url));
  }

  private getAttachmentImageUrl(
    attachment: any,
  ): { displayUrl: string; detectionUrl: string } | null {
    const url =
      attachment?.image_src ||
      attachment?.image ||
      attachment?.image_file_path ||
      attachment?.href;
    if (typeof url !== 'string') {
      return null;
    }

    const trimmed = url.trim();
    if (trimmed.length === 0) {
      return null;
    }

    return this.resolveImageUrls(trimmed);
  }

  private resolveImageUrls(url: string): {
    displayUrl: string;
    detectionUrl: string;
  } {
    const absoluteUrl = this.normalizeToAbsoluteUrl(url);
    const parsedUrl = this.tryParseUrl(absoluteUrl);
    const detectionUrl = this.unwrapGetImageUrl(absoluteUrl);
    const displayUrl =
      parsedUrl && this.isGetImageProxyUrl(parsedUrl)
        ? absoluteUrl
        : this.buildGetImageProxyUrl(detectionUrl);

    return { displayUrl, detectionUrl };
  }

  private normalizeToAbsoluteUrl(url: string): string {
    if (url.startsWith('//')) {
      return `${window.location.protocol}${url}`;
    }

    if (url.startsWith('/')) {
      return `${window.location.origin}${url}`;
    }

    return url;
  }

  private unwrapGetImageUrl(url: string): string {
    let currentUrl = url;
    let parsedUrl = this.tryParseUrl(currentUrl);
    while (parsedUrl && this.isGetImageProxyUrl(parsedUrl)) {
      const originalUrl = parsedUrl.searchParams.get('url');
      if (!originalUrl) {
        break;
      }

      currentUrl = this.normalizeToAbsoluteUrl(originalUrl);
      parsedUrl = this.tryParseUrl(currentUrl);
    }

    return currentUrl;
  }

  private buildGetImageProxyUrl(imageUrl: string): string {
    return `/api/v1.0/utils/get_image?url=${encodeURIComponent(imageUrl)}`;
  }

  private tryParseUrl(url: string): URL | null {
    try {
      return new URL(url, window.location.origin);
    } catch (error) {
      return null;
    }
  }

  private isGetImageProxyUrl(url: URL): boolean {
    return url.pathname.endsWith('/utils/get_image');
  }

  private buildFaceMeta(face: Face): FaceMeta {
    const statusDetails = this.getStatusDetails(
      face.backend_recognition_status_id,
    );
    return {
      borderColor: statusDetails.borderColor,
      recognitionStatus: statusDetails.status,
      name: face.backend_recognition_status_id === 3 ? face.person_name : '',
    };
  }

  private buildLogoMeta(logo: LogoDetection): LogoMeta {
    return {
      name: logo.brand || '',
      expandList: false,
    };
  }

  private normalizeLogo(logo: LogoDetection): LogoDetection {
    const coords = logo?.coordinates ?? ({} as LogoDetection['coordinates']);
    const xmin = coords.xmin ?? coords.x ?? 0;
    const ymin = coords.ymin ?? coords.y ?? 0;
    const xmax =
      coords.xmax ?? (coords.width !== undefined ? xmin + coords.width : xmin);
    const ymax =
      coords.ymax ??
      (coords.height !== undefined ? ymin + coords.height : ymin);
    const width = coords.width ?? Math.max(0, xmax - xmin);
    const height = coords.height ?? Math.max(0, ymax - ymin);

    return {
      ...logo,
      coordinates: {
        ...coords,
        xmin,
        ymin,
        xmax,
        ymax,
        width,
        height,
      },
    };
  }

  private getStatusDetails(statusId: number): {
    borderColor: string;
    status: FaceRecognitionStatus;
  } {
    switch (statusId) {
      case 2:
        return { borderColor: '#828598', status: 'ignored' };
      case 3:
        return { borderColor: '#09bc8a', status: 'recognized' };
      case 1:
      default:
        return { borderColor: '#ffb400', status: 'unrecognized' };
    }
  }
}
